import { sessionState } from '../../state';
import { AGENT_LABELS } from '../../constants';
import { send } from '../../ws/connection';
import { createUserMetaBar } from '../message-meta-bar';
import { appendMessageAttachments } from './attachments';
import { copyText } from './clipboard';
import { hasQuickMessage, saveQuickMessage } from './context';
import { getMessagesDiv } from './dom';
import { renderMarkdown } from './markdown';
import { scrollToBottom } from './scrollbar';
import type { ChatMessage, MessageRole } from './types';

function normalizeOutlineText(text: string): string {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function getAgentLabel(message: ChatMessage | null): string {
  const agent = String(message?.agent || sessionState.currentAgent || '').trim();
  return AGENT_LABELS[agent] || agent || 'Agent';
}

function getMessageSenderLabel(role: MessageRole, message: ChatMessage | null): string {
  return role === 'user' ? 'You' : getAgentLabel(message);
}

export function createTextSegmentElement(text = '', options: { phase?: 'process' | 'final' } = {}): HTMLDivElement {
  const phase = options.phase === 'process' ? 'process' : 'final';
  const textDiv = document.createElement('div');
  textDiv.className = `msg-text msg-segment msg-segment-text msg-segment-${phase}`;
  textDiv.dataset.phase = phase;
  textDiv.dataset.rawText = text;
  textDiv.innerHTML = text ? renderMarkdown(text) : '';
  return textDiv;
}

function appendSystemRetryAction(bubble: HTMLElement, message: ChatMessage | null): void {
  const action = message?.retryAction;
  if (!bubble || !action || action.used) return;
  if (!['codex_compact_retry', 'codex_interrupted_retry'].includes(action.type || '') || !action.id) return;
  const actions = document.createElement('div');
  actions.className = 'msg-actions';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'msg-action-btn';
  btn.textContent = action.label || '清线程并携带历史重试';
  btn.addEventListener('click', () => {
    btn.disabled = true;
    btn.textContent = '正在重试…';
    send({
      type: 'retry_failed_session',
      sessionId: sessionState.currentSessionId,
      retryActionId: action.id,
    });
  });
  actions.appendChild(btn);
  bubble.appendChild(actions);
}

export function createMsgElement(
  role: MessageRole,
  content: string,
  attachments: unknown[] = [],
  message: ChatMessage | null = null,
): HTMLDivElement {
  const div = document.createElement('div');
  div.className = `msg ${role}`;
  if (role === 'user') {
    div.dataset.outlineText = normalizeOutlineText(content);
    const identity = String(message?.clientMessageId || message?.timestamp || '').trim();
    if (identity) div.dataset.messageIdentity = identity;
  }

  if (role === 'system') {
    const bubble = document.createElement('div');
    bubble.className = 'msg-bubble';
    bubble.textContent = content;
    appendSystemRetryAction(bubble, message);
    div.appendChild(bubble);
    return div;
  }

  const avatar = document.createElement('div');
  avatar.className = 'msg-avatar';
  avatar.textContent = role === 'user' ? 'U' : (sessionState.currentAgent === 'codex' ? 'O' : 'C');

  const senderLabel = document.createElement('div');
  senderLabel.className = 'msg-sender-label';
  senderLabel.textContent = getMessageSenderLabel(role, message);

  const bubble = document.createElement('div');
  bubble.className = 'msg-bubble';

  if (role === 'user') {
    if (content) {
      const textNode = document.createElement('div');
      textNode.className = 'msg-text';
      textNode.style.whiteSpace = 'pre-wrap';
      textNode.textContent = content;
      bubble.appendChild(textNode);
    }
    appendMessageAttachments(bubble, attachments);
  } else {
    bubble.innerHTML = content ? renderMarkdown(content) : '';
    appendMessageAttachments(bubble, attachments);
  }

  if (role === 'user') {
    const header = document.createElement('div');
    header.className = 'msg-header';
    header.appendChild(avatar);
    header.appendChild(senderLabel);
    div.appendChild(header);
    div.appendChild(bubble);
    bubble.appendChild(createUserMetaBar({
      content,
      message,
      copyText,
      saveQuickMessage,
      hasQuickMessage,
    }));
  } else {
    const contentCol = document.createElement('div');
    contentCol.className = 'msg-content-col';
    contentCol.appendChild(senderLabel);
    contentCol.appendChild(bubble);
    div.appendChild(avatar);
    div.appendChild(contentCol);
  }
  return div;
}

export function appendUserMessage(text: string, attachments: unknown[] = [], message: ChatMessage | null = null): void {
  const messagesDiv = getMessagesDiv();
  if (!messagesDiv) return;
  const welcome = messagesDiv.querySelector('.welcome-msg');
  if (welcome) welcome.remove();
  messagesDiv.appendChild(createMsgElement('user', text, attachments, message));
  scrollToBottom(true);
}
